// Helper utilities for Savidna CRM

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP'
  }).format(amount);
};
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Santo_Domingo'
  });
};
export const formatShortDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Santo_Domingo'
  });
};
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};
export const formatDateTime = (date: string, time: string): string => {
  return `${formatDate(date)} a las ${formatTime(time)}`;
};
export const getToday = (): string => {
  const now = new Date();
  // Use Dominican Republic timezone
  const parts = now.toLocaleDateString('en-CA', {
    timeZone: 'America/Santo_Domingo'
  });
  return parts; // Returns YYYY-MM-DD format
};
export const getTomorrow = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const parts = tomorrow.toLocaleDateString('en-CA', {
    timeZone: 'America/Santo_Domingo'
  });
  return parts;
};
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};
export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};
export const getMonthName = (month: number): string => {
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return months[month];
};
export const getDayName = (day: number): string => {
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  return days[day];
};
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'programada':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'completada':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'cancelada':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'reprogramada':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};
export const getStageColor = (stage: string): string => {
  switch (stage) {
    case 'nuevo_lead':
      return 'bg-blue-500';
    case 'contactado':
      return 'bg-cyan-500';
    case 'evaluacion_consulta':
      return 'bg-purple-500';
    case 'cotizacion_enviada':
      return 'bg-amber-500';
    case 'confirmado':
      return 'bg-emerald-500';
    case 'cita_agendada':
      return 'bg-teal-500';
    case 'servicio_realizado':
      return 'bg-green-600';
    default:
      return 'bg-gray-500';
  }
};
export const getStageName = (stage: string): string => {
  switch (stage) {
    case 'nuevo_lead':
      return 'Nuevo Lead';
    case 'contactado':
      return 'Contactado';
    case 'evaluacion_consulta':
      return 'Evaluación / Consulta';
    case 'cotizacion_enviada':
      return 'Cotización Enviada';
    case 'confirmado':
      return 'Confirmado';
    case 'cita_agendada':
      return 'Cita Agendada';
    case 'servicio_realizado':
      return 'Servicio Realizado';
    default:
      return stage;
  }
};
export const getStatusName = (status: string): string => {
  switch (status) {
    case 'programada':
      return 'Programada';
    case 'completada':
      return 'Completada';
    case 'cancelada':
      return 'Cancelada';
    case 'reprogramada':
      return 'Reprogramada';
    default:
      return status;
  }
};
export function getRoleName(role: string): string {
  // Roles base conocidos
  const roleNames: Record<string, string> = {
    administrador: 'Administrador'
  };

  // Si es un rol conocido, devolver su nombre
  if (roleNames[role]) return roleNames[role];

  // Si no, intentar buscar en roles dinámicos guardados en localStorage
  try {
    const savedRoles = localStorage.getItem('savidna-roles');
    if (savedRoles) {
      const roles = JSON.parse(savedRoles);
      const found = roles.find((r: any) => r.name === role);
      if (found) return found.displayName || found.name;
    }
  } catch (e) {}

  // Capitalizar la primera letra como fallback
  return role.charAt(0).toUpperCase() + role.slice(1);
}
export const getGenderName = (gender: string): string => {
  switch (gender) {
    case 'masculino':
      return 'Masculino';
    case 'femenino':
      return 'Femenino';
    case 'otro':
      return 'Otro';
    default:
      return gender;
  }
};
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
export const debounce = <T extends (...args: unknown[]) => void,>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Helper to get the day of week name (in English, for schedule keys) from a date string
export const getDayOfWeekKey = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayIndex = date.getDay();
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayKeys[dayIndex];
};

// Helper to get Spanish day name from a date string
export const getDayOfWeekName = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayIndex = date.getDay();
  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return dayNames[dayIndex];
};

// Helper to get the correct prefix for a doctor based on gender and specialty/role
export const getDoctorPrefix = (gender?: string, specialtyOrRole?: string): string => {
  // If the specialty or role explicitly indicates a therapist profession, use "Tera."
  // But NOT for compound words like "Sueroterapía", "Ozonoterapia", "Quimioterapia", etc.
  if (specialtyOrRole) {
    const normalized = specialtyOrRole.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Only match explicit therapist professions:
    // - "terapeuta" or "fisioterapeuta" (the person IS a therapist)
    // - "terapia" as a standalone word or at the start (e.g., "Terapia Física", "Terapia Ocupacional")
    // Do NOT match compound words like "sueroterapia", "ozonoterapia", "quimioterapia"
    const isTherapist = normalized.includes('terapeuta') || normalized.includes('fisioterapeut') || /(?:^|[\s,/])terapia(?:[\s,/]|$)/.test(normalized);
    if (isTherapist) {
      return 'Tera.';
    }
  }
  if (gender === 'femenino') return 'Dra.';
  return 'Dr.';
};

// Module name translations for audit log
export const getModuleNameInSpanish = (module: string): string => {
  const translations: Record<string, string> = {
    patients: 'Pacientes',
    doctors: 'Doctores',
    appointments: 'Citas',
    services: 'Servicios',
    insurance: 'Seguros',
    insurances: 'Seguros',
    specialties: 'Especialidades',
    contacts: 'Contactos',
    companies: 'Empresas',
    opportunities: 'Oportunidades',
    clinicalHistory: 'Historial Clínico',
    clinical_histories: 'Historial Clínico',
    followUpNotes: 'Notas de Seguimiento',
    follow_up_notes: 'Notas de Seguimiento',
    auditLog: 'Bitácora',
    audit_logs: 'Bitácora',
    settings: 'Configuración',
    dashboard: 'Panel de Control',
    roles: 'Roles',
    permissions: 'Permisos'
  };
  return translations[module] || module.charAt(0).toUpperCase() + module.slice(1);
};